/**
 * SentryAngularAdapter.js
 * Angular 1.x compatibility adapter for Sentry v8
 * 
 * Provides a bridge between the new SentryService and existing Angular code
 * Maintains backward compatibility during migration period
 */

import { sentryService } from './SentryService';

/**
 * Angular Sentry Adapter
 * Wraps SentryService to work with Angular's DI system
 */
class SentryAngularAdapter {
  constructor($window, $state, leanLawAuth) {
    this.$window = $window;
    this.$state = $state;
    this.leanLawAuth = leanLawAuth;
    this.initialized = false;
  }

  /**
   * Initialize Sentry with Angular-specific context
   */
  async initialize(userData, meData) {
    if (this.initialized) {
      console.warn('[SentryAngularAdapter] Already initialized');
      return;
    }
    const environment = this._determineEnvironment();

    // Use webpack-injected DSN when available
    let dsn;
    try {
      // eslint-disable-next-line no-undef
      dsn = typeof __SENTRY_DSN__ !== 'undefined' ? __SENTRY_DSN__ : undefined;
    } catch {
      dsn = undefined;
    }

    // In development, allow initialization without DSN for testing
    const isDev = environment === 'development';
    console.log('[SentryAngularAdapter] initialize() context', {
      environment,
      nodeEnv: process.env.NODE_ENV,
      dsn: dsn ? 'configured' : 'not configured',
      domain: this.leanLawAuth?.domain || 'unknown',
      hasUserData: Boolean(userData),
      hasMeData: Boolean(meData)
    });
    if (!dsn && !isDev) {
      console.warn('[SentryAngularAdapter] SENTRY_DSN not configured in production; skipping initialization');
      return;
    }
    if (!dsn && isDev) {
      console.warn('[SentryAngularAdapter] ⚠️  SENTRY_DSN not configured');
      console.warn('  To enable Sentry locally, run: SENTRY_DSN=<your-dsn> npm start');
      console.warn('  Sentry test utilities will still be available for testing.');
    }
    try {
      await sentryService.initialize({
        dsn,
        environment,
        user: this._transformUserData(userData, meData),
        enableReplays: meData?.features?.targetedReplays || false
      });

      // Set up Angular-specific tracking
      this._setupAngularTracking();
      this.initialized = true;
      console.log('[SentryAngularAdapter] Initialized', {
        environment,
        hasStateChangeHandler: Boolean(this._trackStateChange)
      });
    } catch (error) {
      console.error('[SentryAngularAdapter] Initialization failed:', error);
    }
  }

  /**
   * Transform user data to Sentry format
   */
  _transformUserData(userData, meData) {
    return {
      id: userData.myleanlawId || userData.id,
      myleanlaw_id: userData.myleanlawId || userData.id,
      email: userData.email,
      user_name: userData.name,
      user_role: userData.role,
      created_at: userData.created_at,
      registrant: userData.registrant,
      impersonation: userData.impersonation,
      company_id: userData.companyId,
      company_name: userData.companyName,
      company_status: userData.companyStatus,
      group_type: userData.group_type,
      group_value: userData.group_value
    };
  }

  /**
   * Determine environment based on Angular context
   */
  _determineEnvironment() {
    const environment = (process.env.NODE_ENV || 'production').toLowerCase();
    const domain = this.leanLawAuth?.domain || '';
    const testOrStaging = domain.indexOf('test') >= 0 || domain.indexOf('stag') >= 0;
    if (environment === 'production' && testOrStaging) {
      return 'test';
    }
    if (environment === 'development' || domain.indexOf('localhost') >= 0 || this.$window.location.hostname.indexOf('localhost') >= 0) {
      return 'development';
    }
    return environment;
  }

  /**
   * Setup Angular-specific tracking (state changes, etc.)
   */
  _setupAngularTracking() {
    // Track Angular state changes
    if (this.$state) {
      this._trackStateChange = (event, toState, toParams, fromState) => {
        if (!toState || toState.redirectTo) return;

        // Start a named "navigation" performance transaction from the
        // ui-router state. Sentry's automatic history-based navigation
        // instrumentation doesn't fire for this app's hashbang route changes
        // (see the doc comment on SentryService.startNavigationSpan for why)
        // and, even where it does fire, names spans from
        // window.location.pathname - a constant string for every route in a
        // hashbang SPA. Without this, every span reports transaction "/".
        sentryService.startNavigationSpan(toState.name);

        // Add breadcrumb for navigation
        sentryService.addBreadcrumb({
          type: 'navigation',
          category: 'angular-state',
          message: `Navigation: ${fromState?.name || 'initial'} -> ${toState.name}`,
          level: 'info',
          data: {
            from: fromState?.name,
            to: toState.name,
            params: this._sanitizeParams(toParams)
          }
        });

        // Set route context
        sentryService.setContext('route', {
          state: toState.name,
          url: toState.url
          // Don't include params (may contain sensitive data)
        });
      };
      console.log('[SentryAngularAdapter] Angular state tracking enabled');
    } else {
      console.warn('[SentryAngularAdapter] Angular state tracking not enabled: $state unavailable');
    }
  }

  /**
   * Sanitize route params (remove sensitive data)
   */
  _sanitizeParams(params) {
    if (!params) return {};
    const sanitized = {};
    Object.keys(params).forEach(key => {
      // Only include non-sensitive IDs
      if (key.endsWith('Id') || key === 'id') {
        sanitized[key] = params[key];
      }
    });
    return sanitized;
  }

  /**
   * Capture exception
   */
  captureException(error, context = {}) {
    return sentryService.captureException(error, {
      ...context,
      framework: 'angular',
      state: this.$state?.current?.name
    });
  }

  /**
   * Capture message
   */
  captureMessage(message, level = 'info', context = {}) {
    return sentryService.captureMessage(message, level, {
      ...context,
      framework: 'angular',
      state: this.$state?.current?.name
    });
  }

  /**
   * Add breadcrumb
   */
  addBreadcrumb(breadcrumb) {
    return sentryService.addBreadcrumb(breadcrumb);
  }

  /**
   * Set user context
   */
  setUser(userData) {
    return sentryService.setUser(userData);
  }

  /**
   * Set context
   */
  setContext(name, context) {
    return sentryService.setContext(name, context);
  }

  /**
   * Set tag
   */
  setTag(key, value) {
    return sentryService.setTag(key, value);
  }

  /**
   * Get state change handler (for Angular rootScope)
   */
  getStateChangeHandler() {
    return this._trackStateChange;
  }
}

/**
 * Angular Factory for SentryAngularAdapter
 */
export function createSentryAngularAdapter($window, $state, leanLawAuth) {
  return new SentryAngularAdapter($window, $state, leanLawAuth);
}
export default SentryAngularAdapter;